<template>
  <div class="row scrollable events participants">
    <div class="col-12 mb-3 mt-3">
      <h3>{{ $t('pages.admin.Participants') }}</h3>
    </div>
    <loading :name="loaderName" class="col-12">
      <div class="col-12">
        <div class="form-inline row">
          <div class="form-group col-xl-6 col-12 mt-3">
            <input
              class="form-control w-100"
              :placeholder="$t('fields.Search')"
              v-model="filter"
              @input="($event) => { filter = $event.target.value; filterResults()}"
            />
          </div>
          <div class="col-12 mt-3">
            <div class="row">
              <div class="form-group col-xl-3 col-6">
                <label class="form-check-label">{{$t('filters.Volunteers')}}</label>
                <multiselect
                  v-model="volunteerFilter"
                  :options="volunteerTypesOptions"
                  :multiple="false"
                  label="label"
                  track-by="value"
                  @input="filterResults"
                  :placeholder="$t('selectField.SelectOption')"
                  :deselect-label="$t('button.Delete')"
                  :select-label="$t('selectField.Select')"
                  :selected-label="$t('selectField.Selected')"
                  :close-on-select="true"
                ></multiselect>
              </div>
              <div class="form-group col-xl-3 col-6">
                <label class="form-check-label">{{$t('filters.Type')}}</label>
                <multiselect
                  v-model="typeFilter"
                  :options="types"
                  :multiple="false"
                  label="label"
                  track-by="value"
                  @input="filterResults"
                  :placeholder="$t('selectField.SelectOption')"
                  :deselect-label="$t('button.Delete')"
                  :select-label="$t('selectField.Select')"
                  :selected-label="$t('selectField.Selected')"
                  :close-on-select="true"
                ></multiselect>
              </div>
            </div>
          </div>

          <div class="col-12 mt-3">
            <div class="row">
              <div class="form-group col-6 col-xl-3">
                <label class="form-check-label">{{$t('filters.Group')}}</label>
                <multiselect
                  v-model="group"
                  :options="getGroups(this.eventId)"
                  :multiple="false"
                  label="label"
                  track-by="value"
                  @input="filterResults"
                  :placeholder="$t('selectField.SelectOption')"
                  :deselect-label="$t('button.Delete')"
                  :select-label="$t('selectField.Select')"
                  :selected-label="$t('selectField.Selected')"
                  :close-on-select="true"
                ></multiselect>
              </div>
              <div class="form-group col-6 col-xl-3" v-if="variants.length > 1">
                <label class="form-check-label">{{$t('filters.Variants')}}</label>
                <multiselect
                  v-model="variant"
                  :options="variants"
                  :multiple="false"
                  label="label"
                  track-by="value"
                  @input="filterResults"
                  :placeholder="$t('selectField.SelectOption')"
                  :deselect-label="$t('button.Delete')"
                  :select-label="$t('selectField.Select')"
                  :selected-label="$t('selectField.Selected')"
                  :close-on-select="true"
                ></multiselect>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-3">
          <span class="dot" style="background-color: rgba(0, 0, 0, 0.05)"></span>
          {{$t('fields.Participant')}}
          <span class="dot volunteer"></span>
          {{$t('filters.Volunteers')}}
          <span class="dot leader"></span>
          {{$t('fields.Leader')}}
        </div>
      </div>
      <div class="col-12 mt-4">
        <b-table
          v-if="list && list.data"
          striped
          borderless
          small
          hover
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :items="this.loadTableContent"
          :tbody-tr-class="rowClass"
          :current-page="list.current_page"
          @row-clicked="click"
          :fields="fields"
        >
          <template v-slot:cell(note)="data">
            <div v-if="data.item.anote || data.item.admin_note">
              <div class="small text-uppercase">
                <strong>{{$t('fields.AdminNote')}}:</strong>
              </div>
              <span v-html="data.item.anote"></span>
              <span v-html="data.item.admin_note"></span>
            </div>
            <div class="small text-uppercase" v-if="data.item.note">
              <div>
                <strong>{{$t('fields.UserNote')}}:</strong>
              </div>
              <span v-html="data.item.note"></span>
            </div>
          </template>

          <template
            v-slot:cell(birth_date)="data"
          >{{ data.item.birth_date | formatTime('MMM YYYY') }}</template>

          <template v-slot:cell(contact)="data">
            <div>
              <i class="fas fa-at hand" @click.stop.prevent="copy">
                <span>{{data.item.email}}</span>
              </i>
            </div>
            <div>
              <i class="fas fa-phone hand" @click.stop.prevent="copy">
                <span>{{data.item.phone}}</span>
              </i>
            </div>
          </template>

          <template v-slot:cell(participant_count)="data">
              <div class="small text-uppercase">
                <strong>{{$t('fields.AsVolunteer')}}:</strong>
              </div>
              <span v-html="data.item.participant_count"></span>
              <div class="small text-uppercase">
                <strong>{{$t('fields.AsParticipant')}}:</strong>
              </div>
              <span v-html="data.item.volunteer_count"></span>
          </template>
        </b-table>
        <span
          class="float-right"
          v-if="list && list.to && list.total"
        >{{$t('filters.count')}}: {{list.to}}/{{list.total}}</span>
        <paginate
          v-if="list && list.last_page"
          v-model="page"
          :page-count="list.last_page"
          :click-handler="changePage"
          :prev-text="$t('button.Prev')"
          :next-text="$t('button.Next')"
          :page-class="'page-item'"
          :container-class="'pagination justify-content-center'"
        ></paginate>
      </div>
    </loading>
  </div>
</template>

<script>
import { BTable } from "bootstrap-vue/src/components/table/table";
import { mapActions, mapGetters } from "vuex";
import { ROUTER_NAMES } from "../../constants";
import paginationMixin from "../../mixins/paginationMixin";
import filterMixin from "../../mixins/filterMixin";
import find from "lodash/find";
import { TRANSPORT_OPTIONS } from "../../constants/eventContant";
import Multiselect from "vue-multiselect";

export default {
  name: "Participants",
  mixins: [paginationMixin, filterMixin],
  components: {
    Multiselect,
    BTable
  },
  data() {
    return {
      filter: "",
      group: null,
      variant: null,
      filters: [],
      sortBy: "birth_date",
      sortDesc: true,
      routeName: ROUTER_NAMES.ADMIN_EVENTS_PARTICIPANTS,
      loaderName: "load participants",
      clearAction: "clearParticipants",
      volunteerFilter: null,
      typeFilter: null,
      types: [
        {
          value: "volunteer",
          label: this.$t("filters.options.volunteers")
        },
        {
          value: "participant",
          label: this.$t("filters.options.participants")
        },
        {
          value: "was_on_event",
          label: this.$t("filters.options.was_on_event")
        },
        {
          value: "not_was_on_event",
          label: this.$t("filters.options.not_was_on_event")
        }
      ],
      fields: [
        {
          key: "last_name",
          label: this.$t("fields.FullName"),
          sortable: true,
          formatter: (value, field, row) => {
            return `${row.first_name} ${value}`;
          }
        },
        {
          label: this.$t("fields.BirthDate"),
          key: "birth_date",
          sortable: true
        },
        {
          key: "contact",
          label: this.$t("fields.Contacts"),
          sortable: false
        },
        {
          key: "note",
          label: this.$t("fields.Note"),
          sortable: true
        },
        {
          key: "volunteer_type_id",
          label: this.$t("fields.volunteerType"),
          sortable: false,
          formatter: value => {
            return this.volunteerType(value);
          }
        },
        {
          key: "group_name",
          label: this.$t("fields.group"),
          sortable: true
        },
        {
          key: "participant_count",
          label: this.$t("fields.ParticipantCount"),
          sortable: false
        },
        {
          key: "volunteer_count",
          label: this.$t("fields.VolunteerCount"),
          sortable: false
        }
      ]
    };
  },
  created() {
    this.$wait.start(this.loaderName);
    const types = this.loadVolunteerTypes();
    const groups = this.loadGroups({
      eventId: this.eventId
    });
    const detail = this.detailEvent({
      eventId: this.eventId
    });
    Promise.all([types, groups, detail]).then(() => {
      const groups = this.getGroups(this.eventId);

      this.filters = [
        {
          name: "p",
          type: "string"
        },
        {
          name: "typeFilter",
          type: "select",
          options: this.types
        },
        {
          name: "volunteerFilter",
          type: "select",
          valueType: "int",
          options: this.volunteerTypesOptions
        },
        {
          name: "filter",
          type: "string"
        },
        {
          name: "group",
          type: "select",
          valueType: "int",
          options: groups
        },
        {
          name: "variant",
          type: "select",
          options: this.variants
        }
      ];
      this.resolveFilters();
      return this.loadContent().then(() => {
        this.$wait.end(this.loaderName);
      });
    });
  },
  computed: {
    ...mapGetters(["getGroups"]),
    eventId() {
      return this.$route.params.eventId;
    },
    event() {
      return this.$store.state.admin.eventDetail[this.eventId];
    },
    // eslint-disable-next-line vue/no-dupe-keys
    fields() {
      let fields = [
        {
          key: "last_name",
          label: this.$t("fields.FullName"),
          sortable: true,
          formatter: (value, field, row) => {
            return `${row.first_name} ${value}`;
          }
        },
        {
          label: this.$t("fields.BirthDate"),
          key: "birth_date",
          sortable: true
        },
        {
          label: this.$t("fields.City"),
          key: "city",
          sortable: false
        },
        {
          key: "contact",
          label: this.$t("fields.Contacts"),
          sortable: false
        },
        {
          key: "note",
          label: this.$t("fields.Note"),
          sortable: true
        },
        {
          key: "volunteer_type_id",
          label: this.$t("fields.volunteerType"),
          sortable: false,
          formatter: value => {
            return this.volunteerType(value);
          }
        },
        {
          key: "group_name",
          label: this.$t("fields.group"),
          sortable: true
        },
        {
          key: "participant_count",
          label: this.$t("fields.ParticipationCount"),
          sortable: false
        }
      ];
      if (this.event.type === "pz") {
        fields.push({
          key: "want_to_be_animator_on_pz",
          label: this.$t("fields.wantBeAnimatorOnPZShort"),
          sortable: false,
          formatter: (value) => {
              return value == 1 ? 'Ano' : 'Nie'
          }
        });
      }

      return fields;
    },
    variants() {
      return this.event && this.event.prices
        ? this.event.prices.map(price => {
            return {
              label: price.description,
              value: price.id
            };
          })
        : [];
    },
    items() {
      return this.$store.state.admin.participants[this.eventId];
    },
    volunteerTypes() {
      return this.$store.state.admin.volunteerTypes;
    },
    volunteerTypesOptions() {
      return this.event && this.event.volunteerTypes
        ? this.event.volunteerTypes.map(type => {
            return {
              label: type.name,
              value: type.id
            };
          })
        : [];
    }
  },
  methods: {
    ...mapActions([
      "loadParticipants",
      "loadVolunteerTypes",
      "detailEvent",
      "loadGroups"
    ]),
    loadTableContent(ctx) {
      return this.loadContent().then(() => {
        return this.list && this.list.data ? this.list.data : [];
      });
    },
    rowClass(participant, type) {
      if (!participant) return;
      if (participant.is_leader == 1) return "leader";
      if (participant.volunteer_type_id != null) return "volunteer";
    },
    loadContent(page) {
      return this.loadParticipants({
        page: page || this.page,
        eventId: this.eventId,
        filter: this.filter,
        variant: this.variant ? this.variant.value : null,
        sortBy: this.sortBy,
        group: this.group ? this.group.value : null,
        type: this.typeFilter ? this.typeFilter.value : null,
        sortDesc: this.sortDesc,
        volunteers: this.volunteerFilter ? this.volunteerFilter.value : null
      });
    },
    copy(e) {
      this.selectText(e.target);
      document.execCommand("copy");
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      } else {
        document.selection.empty();
      }
      this.$toasted.show("Skopirované", {
        duration: 1000,
        icon: "check"
      });
    },
    selectText(element) {
      var range;
      if (document.selection) {
        // IE
        range = document.body.createTextRange();
        range.moveToElementText(element);
        range.select();
      } else if (window.getSelection) {
        range = document.createRange();
        range.selectNode(element);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    },
    transportType(transport) {
      const transportType = find(TRANSPORT_OPTIONS, { value: transport });
      return transportType
        ? transportType.label
        : this.$t("transportTypes.Bus") + ": " + transport;
    },
    volunteerType(typeId) {
      const type = find(this.event.volunteerTypes, type => type.id == typeId);
      return type ? type.name : "";
    },
    click(participant) {
      return this.$router.push(
        "/admin/events/" +
          participant.event_id +
          "/participants/" +
          participant.user_id +
          "/edit"
      );
    }
  }
};
</script>

<style scoped>
.fas span {
  font-family: "Raleway", sans-serif;
  font-weight: normal;
  padding-left: 5px;
}

td .fas {
  margin-left: -15px;
}
</style>
